import { Controller } from '@hotwired/stimulus';
import { TOMSELECT_CHANGE, TOMSELECT_CLOSE, TOMSELECT_OPEN } from '../js/events.js';

export default class TomselectController extends Controller {
    /**
     * @returns {HTMLElement|null}
     */
    get form() {
        return this.element.closest('form');
    }

    /**
     * @returns {HTMLElement|null}
     */
    get placeholder() {
        let element = this.element.nextElementSibling;
        while (element) {
            if (element.classList.contains('ts-init-placeholder')) {
                return element;
            }

            element = element.nextElementSibling;
        }

        return null;
    }

    connect() {
        this.element.addEventListener('autocomplete:pre-connect', this._onPreConnect);
    }

    disconnect() {
        this.element.removeEventListener('autocomplete:pre-connect', this._onPreConnect);
        this.form?.removeEventListener('reset', this._onReset);
    }

    _onReset = () => {
        if (this.element.tomselect) {
            this.element.tomselect.clear();
        }
    };

    _onPreConnect = (event) => {
        const options = event.detail.options;

        this._removeStaleWidget();
        this._rememberOriginalTabindex();

        // change default rendering to make custom styling easier
        options.render.item = function (item) {
            return `<div><div class="text">${item.text}</div></div>`;
        };

        options.onDropdownOpen  = this._onOpen;
        options.onDropdownClose = this._onClose;

        if (this._hasPlugin(options, 'fify_items')) {
            // we cannot use a real plugin here because it doesn't work with upstream controller in UX Autocomplete
            this._removePlugin(options, 'fify_items')
            this._initItemsPlugin(options);

        } else {
            this._initWithoutItems(options);
            if (this.element.multiple) {
                // add convenient plugin for navigation between items
                this._ensurePlugin(options, 'caret_position');
            }
        }

        this.form?.addEventListener('reset', this._onReset);
    };

    /**
     * Removes stale TomSelect markup left by a restored Turbo snapshot.
     *
     * A bug happens otherwise when someone uses the browser back button...
     *
     * @private
     */
    _removeStaleWidget() {
        const element = this.element;

        // A live widget is left alone
        if (!element.classList.contains('tomselected') || element.tomselect) {
            return;
        }

        const wrapper = element.nextElementSibling;

        if (wrapper?.classList.contains('ts-wrapper')) {
            wrapper.remove();
        }

        element.classList.remove('tomselected', 'ts-hidden-accessible');

        this._restoreOriginalTabindex();
    }

    /**
     * TomSelect overwrites tabindex with -1, so the value has to be stashed while it is
     * still visible.
     *
     * @private
     */
    _rememberOriginalTabindex() {
        const element = this.element;

        // '' records "there was no attribute", which has to stay distinct from a real value
        if (element.dataset.fifyTabindex === undefined) {
            element.dataset.fifyTabindex = element.getAttribute('tabindex') ?? '';
        }
    }

    /**
     * @private
     */
    _restoreOriginalTabindex() {
        const element  = this.element;
        const original = element.dataset.fifyTabindex;

        // nothing stashed (snapshot predates this), or there was no attribute to put back
        if (original === undefined || original === '') {
            element.removeAttribute('tabindex');

            return;
        }

        element.setAttribute('tabindex', original);
    }

    _initWithoutItems(options) {
        const onInitialize = options.onInitialize;
        const placeholder  = this.placeholder;

        options.onInitialize = function () {
            if (onInitialize) {
                onInitialize();
            }

            if (placeholder) {
                placeholder.remove();
            }
        };
    }

    /**
     * @param {Object} options
     * @private
     */
    _initItemsPlugin(options) {
        this._ensurePlugin(options, 'dropdown_input');
        this._ensurePlugin(options, 'checkbox_options');
        this._ensurePlugin(options, 'no_backspace_delete');
        this._ensurePlugin(options, 'clear_button');
        options.closeAfterSelect = false;

        const onInitialize = options.onInitialize;
        const onItemAdd    = options.onItemAdd;
        const onItemRemove = options.onItemRemove;
        const placeholder  = this.placeholder;

        let item;
        let counter;

        const inputPlaceholder = options.search_placeholder;
        delete options.search_placeholder;

        const itemCount = function(select) {
            if (!item || !counter) {
                return;
            }

            const itemsCount   = select.items.length;
            const selectedItem = itemsCount > 0 ? select.options[select.items[0]] : null;

            if (itemsCount === 0 || !selectedItem) {
                item.dataset.fifyItemsHidden    = 'true';
                counter.dataset.fifyItemsHidden = 'true';

                return;
            }

            item.innerText    = selectedItem.text;
            counter.innerText = itemsCount > 1 ? `+${itemsCount - 1}` : '';

            delete item.dataset.fifyItemsHidden;
            delete counter.dataset.fifyItemsHidden;
        }

        options.onInitialize = function () {
            if (onInitialize) {
                onInitialize();
            }

            if (placeholder) {
                placeholder.remove();
            }

            if (typeof this === 'undefined'
                || !this.control || !this.wrapper
            ) {
                // TomSelect has not been initialized correctly
                return;
            }

            if (inputPlaceholder && this.control_input) {
                this.control_input.placeholder = inputPlaceholder;
            }

            this.wrapper.classList.add('plugin-fify_items');

            item = document.createElement('div');
            item.classList.add('text');

            const itemWrapper = document.createElement('div');
            itemWrapper.classList.add('item');
            itemWrapper.append(item);

            counter = document.createElement('div');
            counter.classList.add('counter');

            const button = this.control.querySelector('.clear-button');

            if (button) {
                button.insertAdjacentElement('beforebegin', itemWrapper);
                button.insertAdjacentElement('beforebegin', counter);
            } else {
                this.control.append(itemWrapper);
                this.control.append(counter);
            }

            itemCount(this);
        };

        options.onItemAdd = function () {
            if (onItemAdd) {
                onItemAdd();
            }

            itemCount(this);
        };

        options.onItemRemove = function () {
            if (onItemRemove) {
                onItemRemove();
            }

            itemCount(this);
        };

        options.render.item = function () {
            return '<span data-fify-items-hidden></span>';
        };
    }

    /**
     * @param {Object} options
     * @param {String} plugin
     * @private
     */
    _ensurePlugin(options, plugin) {
        if (this._hasPlugin(options, plugin)) {
            return;
        }

        if (!options.plugins) {
            options.plugins = {};
        }

        if (Array.isArray(options.plugins)) {
            if (options.plugins.indexOf(plugin) < 0) {
                options.plugins.push(plugin);
            }

            return;
        }

        options.plugins[plugin] = {};
    }

    /**
     * @param {Object} options
     * @param {String} plugin
     * @private
     */
    _removePlugin(options, plugin) {
        if (!options.plugins) {
            return;
        }

        if (Array.isArray(options.plugins)) {
            const pos = options.plugins.indexOf(plugin);

            if (pos > -1) {
                options.plugins.splice(pos, 1);
            }
        } else if (typeof options.plugins === 'object') {
            delete options.plugins[plugin];
        }
    }

    /**
     * @param {Object} options
     * @param {String} plugin
     * @private
     */
    _hasPlugin(options, plugin) {
        if (!options.plugins) {
            return;
        }

        return (Array.isArray(options.plugins) && options.plugins.indexOf(plugin) > -1)
            || (typeof options.plugins === 'object' && plugin in options.plugins);
    }


    /**
     * @param {String} name
     * @param {Object} [data={}]
     * @private
     */
    _dispatchEvent(name, data = {}) {
        const event = new CustomEvent(name, {
            bubbles: true,
            detail: {
                input:     this.element,
                tomselect: this.element.tomselect,
                ...data,
            },
        });

        this.element.dispatchEvent(event);
    }

    _currentValue = null;

    _onOpen = () => {
        const value        = this.element.tomselect.getValue();
        this._currentValue = Array.isArray(value) ? Array.from(value) : value;

        this._dispatchEvent(TOMSELECT_OPEN);
    };

    _onClose = () => {
        const newValue = this.element.tomselect.getValue();
        let isChanged;

        if (Array.isArray(newValue) && Array.isArray(this._currentValue)) {
            isChanged = this._isArrayChanged(this._currentValue, newValue);
        } else {
            isChanged = newValue !== this._currentValue;
        }

        this._dispatchEvent(TOMSELECT_CLOSE, { isChanged });

        if (isChanged) {
            this._dispatchEvent(TOMSELECT_CHANGE, {
                oldValue: this._currentValue,
                newValue: newValue,
            });
        }
    };

    /**
     *
     * @param {Array} oldArray
     * @param {Array} newArray
     * @returns {Boolean}
     * @private
     */
    _isArrayChanged(oldArray, newArray) {
        if (oldArray.length !== newArray.length) {
            return true;
        }

        for (const item of oldArray) {
            if (newArray.indexOf(item) === -1) {
                return true;
            }
        }

        for (const item of newArray) {
            if (oldArray.indexOf(item) === -1) {
                return true;
            }
        }

        return false;
    }
}
